import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, ThumbsUp, Award, ArrowUpDown, ArrowUp, ArrowDown, Archive, ArchiveRestore } from 'lucide-react';
import { format } from 'date-fns';

export default function CapabilityTableView({
  capabilities,
  officers,
  user,
  isSeniorOfficer,
  onEdit,
  onDelete,
  onApprove,
  onInstitutionalize,
  onArchive,
  onUnarchive,
  deleteConfirm
}) {
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-400" />;
  };

  const statusColors = {
    requested: 'bg-slate-500/20 text-slate-400',
    in_development: 'bg-blue-500/20 text-blue-400',
    contracted: 'bg-purple-500/20 text-purple-400',
    approved: 'bg-emerald-500/20 text-emerald-400',
    institutionalized: 'bg-indigo-500/20 text-indigo-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    deprecated: 'bg-red-500/20 text-red-400'
  };

  const typeColors = {
    contracted: 'bg-purple-500/20 text-purple-400',
    acquired: 'bg-blue-500/20 text-blue-400',
    developed: 'bg-indigo-500/20 text-indigo-400'
  };

  const sorted = useMemo(() => {
    return [...capabilities].sort((a, b) => {
      let valA, valB;
      switch (sortKey) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'type':
          valA = (a.type || '').toLowerCase();
          valB = (b.type || '').toLowerCase();
          break;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        case 'requester':
          const rA = officers.find(o => o.user_email === a.requested_by);
          const rB = officers.find(o => o.user_email === b.requested_by);
          valA = (rA?.callsign || a.requested_by || '').toLowerCase();
          valB = (rB?.callsign || b.requested_by || '').toLowerCase();
          break;
        case 'target':
          valA = a.target_completion_date ? new Date(a.target_completion_date).getTime() : 0;
          valB = b.target_completion_date ? new Date(b.target_completion_date).getTime() : 0;
          break;
        default:
          valA = '';
          valB = '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [capabilities, sortKey, sortDir, officers]);

  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                <span className="flex items-center">Name <SortIcon column="name" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('type')}>
                <span className="flex items-center">Type <SortIcon column="type" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                <span className="flex items-center">Status <SortIcon column="status" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('requester')}>
                <span className="flex items-center">Requested By <SortIcon column="requester" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('target')}>
                <span className="flex items-center">Target Date <SortIcon column="target" /></span>
              </TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((capability) => {
              const requester = officers.find(o => o.user_email === capability.requested_by);

              return (
                <TableRow key={capability.id} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell>
                    <span className="text-sm text-slate-200 font-medium">{capability.name}</span>
                    {capability.description && (
                      <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[250px]">{capability.description}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${typeColors[capability.type] || 'bg-slate-500/20 text-slate-400'} border-none text-xs`}>
                      {capability.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[capability.status] || 'bg-slate-500/20 text-slate-400'} border-none text-xs`}>
                      {capability.status?.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {requester?.callsign || capability.requested_by}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {capability.target_completion_date && format(new Date(capability.target_completion_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {isSeniorOfficer && (capability.status === 'requested' || capability.status === 'draft') && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400 hover:text-emerald-300" onClick={() => onApprove(capability.id)} title="Approve">
                          <ThumbsUp className="w-3 h-3" />
                        </Button>
                      )}
                      {isSeniorOfficer && capability.status === 'approved' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-400 hover:text-indigo-300" onClick={() => onInstitutionalize(capability.id)} title="Institutionalize">
                          <Award className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(capability)}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <div className="relative">
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-red-400" onClick={() => deleteConfirm.checkAndConfirm(capability.id, () => onDelete(capability.id))}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        {deleteConfirm.isPendingConfirm(capability.id) && (
                          <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                            Click again
                          </span>
                        )}
                      </div>
                      {capability.status === 'deprecated' ? (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-400 hover:text-amber-300" onClick={() => onUnarchive(capability.id)} title="Undeprecate">
                          <ArchiveRestore className="w-3 h-3" />
                        </Button>
                      ) : (
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-amber-400" onClick={() => onArchive(capability.id)} title="Deprecate">
                          <Archive className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}